import { __decorate } from "tslib";
import { Component, Mixins } from 'vue-property-decorator';
import { SupportTicketType } from '~/db_types/swagger_types';
import BaseVue from '~/nasa_ui/base/BaseVue';
import CurrentUserMixin from '~/nasa_ui/mixins/CurrentUserMixin';
import { AlertType } from '~/nasa_ui/types';
import { clearSupportTicketsCollectionCache, getContacts, postAttachment, postNotification, postSupportTicket } from '~/nasa_ui/utils';
import { AttachmentType } from './../../../db_types/gql_types';
let MenuCosmicSupportTicketTracker = class MenuCosmicSupportTicketTracker extends Mixins(BaseVue, CurrentUserMixin) {
    gitlabCosmicFeedbackSupportEmail = null;
    slackSupportEmail = null;
    showModalCreateSupportTicket = false;
    attachmentIds = [];
    activeUploadBatches = 0;
    formData = {
        description: null,
        subType: null
    };
    isSaving = false;
    previewFiles = [];
    selectedFile = null;
    uploadingFileNames = new Set();
    get isUploading() {
        return this.activeUploadBatches > 0;
    }
    get microsoftFiles() {
        return this.previewFiles.filter((f) => ['officedocument', 'msword', 'excel', 'powerpoint'].some((t) => f.type.includes(t)));
    }
    get nonMicrosoftFiles() {
        return this.previewFiles.filter((f) => !['officedocument', 'msword', 'excel', 'powerpoint'].some((t) => f.type.includes(t)));
    }
    created() {
        this.$listen('ask_a_question', this.uiClickOfCreateQuestion);
        this.$listen('create_bug', this.uiClickOfCreateBug);
        this.$listen('tell_us_about_a_feature', this.uiClickOfCreateFeature);
        this.fetchSupportEmailContactId();
        this.fetchGitlabSupportEmailContactId();
    }
    viewFile(file) {
        this.selectedFile = file;
    }
    triggerDownload(file) {
        if (!file) {
            return;
        }
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
    async createSupportTicket() {
        this.isSaving = true;
        try {
            const body = {
                description: this.formData.description,
                requestId: null,
                attachmentIds: this.attachmentIds,
                subType: this.formData.subType ? this.formData.subType : SupportTicketType.UNCATEGORIZED
            };
            const resp = await postSupportTicket(body);
            await this.sendSupportTicketEmailToSlack(resp.data.id);
            await this.sendSupportTicketEmailToGitlab(resp.data.id);
            this.resetCache();
            this.resetModals();
            this.$notification.add({
                type: AlertType.SUCCESS,
                text: 'Created.'
            });
            this.$message('support_ticket_update');
        }
        catch (err) {
            if (err instanceof Error) {
                this.$errorUtility({
                    err
                });
            }
            else {
                console.log(`Unexpected error: ${err}`);
            }
        }
        finally {
            this.isSaving = false;
        }
    }
    async fetchSupportEmailContactId() {
        const resp = await getContacts({
            email: ['cosmic_customer_feedb-aaaahapcs2c6bo7moss44jn76q@mri-tech.slack.com'],
            take: -1
        });
        const slackSupportContact = resp?.data.results;
        if (slackSupportContact) {
            this.slackSupportEmail = slackSupportContact[0];
        }
    }
    async fetchGitlabSupportEmailContactId() {
        const resp = await getContacts({
            email: ['contact-project+mri-technologies-cosmic-nasa-cosmic-feedback-75487332-issue-@incoming.gitlab.com'],
            take: -1
        });
        const gitlabCosmicFeedbackSupportContact = resp?.data.results;
        if (gitlabCosmicFeedbackSupportContact) {
            this.gitlabCosmicFeedbackSupportEmail = gitlabCosmicFeedbackSupportContact[0];
        }
    }
    async sendSupportTicketEmailToGitlab(feedbackId) {
        if (this.gitlabCosmicFeedbackSupportEmail) {
            // The email subject will become the GitLab issue title.
            // Adding a clear prefix like '[Feedback Ticket]' helps identify these issues.
            const subject = `[Feedback Ticket] COSMIC feedback - ${this.formData.subType || 'uncategorized'}`;
            // The email body (messageHtml) will become the GitLab issue description.
            // Using HTML for rich formatting, which GitLab's Service Desk will convert to Markdown.
            const messageHtml = `
            <p><strong>Reported By:</strong>  ${this.currentUser?.firstName} ${this.currentUser?.lastName}</p>
                        <p>  ${this.currentUser?.email}</p>
            <hr>
            <p><strong>Issue Description:</strong></p>
            <p>${this.formData.description}</p>
            <hr>
            <p><strong>Link to Feedback:</strong> <a href="${window.location.protocol}//${window.location.host}/support_tickets/manage/${feedbackId}">${this.formData.subType || 'uncategorized'} - ${this.formData.description}</a></p>
            <p><strong>Feedback ID:</strong> ${feedbackId}</p>
        `;
            // Provide a plain-text version as a fallback, ensuring basic readability
            // for email clients that might not render HTML perfectly.
            const messageText = `
        Reported By: ${this.currentUser?.firstName} ${this.currentUser?.lastName}

        ---

        Issue Description:
        ${this.formData.description}

        ---

        Link to Feedback: ${window.location.protocol}//${window.location.host}/support_tickets/search?feedbackId=${feedbackId}
        Feedback ID: ${feedbackId}
        `;
            postNotification({
                attributes: {},
                data: {},
                messageText: messageText,
                messageHtml: messageHtml,
                recipientContactId: [this.gitlabCosmicFeedbackSupportEmail.id],
                subject: subject
            });
        }
        return;
    }
    async sendSupportTicketEmailToSlack(feedbackId) {
        if (this.slackSupportEmail) {
            postNotification({
                attributes: {},
                data: {},
                messageText: `${this.formData.subType || 'uncategorized'} - ${this.formData.description}`,
                messageHtml: `<h3>NEW ${this.formData.subType || 'uncategorized'}</h3>
        <p><strong>Reported By:</strong> ${this.currentUser?.firstName} ${this.currentUser?.lastName}</p>

        <p>${this.formData.description}</p>
        
        <a href="${window.location.protocol}//${window.location.host}/support_tickets/manage/${feedbackId}"><strong>View ticket</strong></a>`,
                recipientContactId: [this.slackSupportEmail.id],
                subject: `COSMIC feedback - ${this.formData.subType || 'uncategorized'}`
            });
        }
        return;
    }
    async onFileSelected(files) {
        const rawFiles = Array.isArray(files) ? files : [files];
        const fileList = rawFiles.filter((f) => f);
        if (!fileList.length || !this.currentUserActiveSupportContext) {
            return;
        }
        this.isSaving = true;
        this.activeUploadBatches++;
        try {
            for (const file of fileList) {
                if (this.previewFiles.some((f) => f.name === file.name) || this.uploadingFileNames.has(file.name)) {
                    continue;
                }
                this.uploadingFileNames.add(file.name);
                this.previewFiles.push(file);
                try {
                    const resp = await postAttachment({
                        file: file,
                        description: file.name,
                        type: AttachmentType.SUPPORT_TICKET
                    });
                    if (resp.data?.id) {
                        this.attachmentIds.push(resp.data.id);
                    }
                }
                catch (err) {
                    // Remove file from preview if upload fails
                    const index = this.previewFiles.indexOf(file);
                    if (index > -1) {
                        this.previewFiles.splice(index, 1);
                    }
                    throw err; // Re-throw to be caught by outer catch
                }
                finally {
                    this.uploadingFileNames.delete(file.name);
                }
            }
        }
        catch (err) {
            if (err instanceof Error) {
                this.$errorUtility({
                    err
                });
            }
        }
        finally {
            this.activeUploadBatches--;
            this.isSaving = false;
        }
    }
    removeFile(index) {
        this.previewFiles.splice(index, 1);
        this.attachmentIds.splice(index, 1);
    }
    resetCache() {
        clearSupportTicketsCollectionCache();
    }
    resetModals() {
        this.showModalCreateSupportTicket = false;
        this.previewFiles = [];
        this.attachmentIds = [];
        this.formData.description = null;
        this.selectedFile = null;
    }
    uiClickOfCreateQuestion() {
        this.formData.subType = SupportTicketType.QUESTION;
        this.showModalCreateSupportTicket = true;
    }
    uiClickOfCreateBug() {
        this.formData.subType = SupportTicketType.BUG;
        this.showModalCreateSupportTicket = true;
    }
    uiClickOfCreateFeature() {
        this.formData.subType = SupportTicketType.FEATURE_REQUEST;
        this.showModalCreateSupportTicket = true;
    }
    viewAllIssuesReport() {
        this.$router.push({
            path: `/support_tickets/search`
        });
    }
};
MenuCosmicSupportTicketTracker = __decorate([
    Component
], MenuCosmicSupportTicketTracker);
export default MenuCosmicSupportTicketTracker;
